import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Link } from "react-router-dom";
import { IoMailOutline } from "react-icons/io5";
import { authApi } from "@/features/auth/auth.api";
import { AuthShell } from "@/features/auth/AuthShell";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { FormField } from "@/components/ui/FormField";

const schema = z.object({
  email: z.string().trim().email("Enter a valid email"),
});

type FormValues = z.infer<typeof schema>;

export default function ForgotPasswordPage() {
  const [submitted, setSubmitted] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  async function onSubmit(values: FormValues) {
    // The endpoint always succeeds (it never reveals whether an account exists),
    // so we show the same confirmation regardless.
    await authApi.forgotPassword(values.email).catch(() => undefined);
    setSubmitted(true);
  }

  if (submitted) {
    return (
      <AuthShell
        title="Check your email"
        footer={
          <Link to="/login" className="text-brand-600 hover:underline dark:text-brand-500">
            Back to sign in
          </Link>
        }
      >
        <div className="flex flex-col items-center gap-3 text-center">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-50 text-brand-600 dark:bg-slate-700">
            <IoMailOutline size={24} />
          </span>
          <p className="text-sm text-slate-600 dark:text-slate-300">
            If an account matches that email, we've sent a link to reset your password. The link expires shortly.
          </p>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Forgot password"
      subtitle="Enter your email and we'll send you a reset link."
      footer={
        <Link to="/login" className="text-brand-600 hover:underline dark:text-brand-500">
          Back to sign in
        </Link>
      }
    >
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
        <FormField label="Email" htmlFor="email" error={errors.email?.message} required>
          <Input id="email" type="email" autoComplete="email" placeholder="you@example.com" {...register("email")} />
        </FormField>
        <Button type="submit" isLoading={isSubmitting} className="mt-1 w-full">
          Send reset link
        </Button>
      </form>
    </AuthShell>
  );
}
